import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUserSession } from '../utils/userAuth';

interface PrincipalAuthContextType {
    principalData: any | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    logout: () => void;
    user: any | null;
}

const PrincipalAuthContext = createContext<PrincipalAuthContextType | undefined>(undefined);

export const usePrincipalAuth = () => {
    const context = useContext(PrincipalAuthContext);
    if (!context) {
        throw new Error('usePrincipalAuth must be used within a PrincipalAuthProvider');
    }
    return context;
};

interface PrincipalAuthProviderProps {
    children: ReactNode;
}

export const PrincipalAuthProvider: React.FC<PrincipalAuthProviderProps> = ({ children }) => {
    const [principalData, setPrincipalData] = useState<any | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const handleForcedLogout = useCallback(() => {
        clearUserSession();
        setToken(null);
        setPrincipalData(null);
        navigate('/user/login');
    }, [navigate]);

    // Initialize auth state from localStorage
    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem('userToken');
            const storedPrincipal = localStorage.getItem('principalInfo');

            if (storedToken) {
                setToken(storedToken);
            }

            if (storedPrincipal) {
                try {
                    const principalDataParsed = JSON.parse(storedPrincipal);
                    setPrincipalData(principalDataParsed);
                } catch (error) {
                    clearUserSession();
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, []);

    const logout = useCallback(() => {
        handleForcedLogout();
    }, [handleForcedLogout]);

    const isAuthenticated = !!token;

    return (
        <PrincipalAuthContext.Provider
            value={{
                principalData,
                token,
                isAuthenticated,
                isLoading,
                logout,
                user: null,
            }}
        >
            {children}
        </PrincipalAuthContext.Provider>
    );
};
